var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useState, useCallback, memo } from 'react';
import styled, { css } from '../../styled';
import { Plus, Minus } from '../icons/outline';
import TooltipView from '../tooltip/TooltipView';
import { StyledInput, MessageWrapper, Message, generateStatusStyle, } from '../input';
const Wrapper = styled.div `
  display: ${({ block }) => (block ? 'flex' : 'inline-block')};
  flex-direction: column;
`;
const InputWrapper = styled.div `
  display: flex;
  outline: 0;
  border-radius: 6px;

  ${({ status, disabled, focused }) => generateStatusStyle(status, true, disabled, focused)};

  ${StyledInput} {
    border-radius: unset;
    border-left: solid 0.0625rem ${({ theme }) => theme.colors.INK_300};
    border-top: none !important;
    border-right: none !important;
    border-bottom: none !important;
    width: ${({ block, width }) => (block ? '100%' : `${width}rem`)};
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
    &[type='number'] {
      -moz-appearance: textfield;
    }
  }

  .inputWithIcon-tooltip {
    width: ${({ block, width }) => (block ? '100%' : `${width}rem`)};
  }
`;
const StyledMessage = styled(Message) `
  font-size: 0.875rem;
  color: ${({ theme, status }) => status === 'invalid' ? theme.colors.RED_700 : theme.colors.GREEN_600};
`;
const ButtonWrapper = styled.div `
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: ${({ type }) => type === 'plus' ? '0 5px 5px 0' : '5px 0 0 5px'};

  ${({ inputSize }) => {
    let size;
    switch (inputSize) {
        case 'SMALL':
            size = '1.75rem';
            break;
        case 'MEDIUM':
            size = '2.25rem';
            break;
        case 'LARGE':
            size = '2.5rem';
            break;
        default:
            break;
    }
    return css `
      min-width: ${size};
      min-height: ${size};
    `;
}}

  ${({ type, theme }) => {
    const borderPosition = type === 'plus' ? 'border-left' : 'border-right';
    return css `
      background-color: ${type === 'plus'
        ? theme.colors.PRIMARY_600
        : theme.colors.SNOW_100};
      ${borderPosition}: none !important;
      & svg > path {
        fill: ${type === 'plus'
        ? theme.colors.SNOW_LIGHT
        : theme.colors.INK_600};
      }
    `;
}}

  ${({ disabled, theme, type }) => {
    return disabled
        ? css `
          cursor: not-allowed;
          background-color: ${type === 'plus'
            ? theme.colors.PRIMARY_300
            : theme.colors.SNOW_DISABLED};
        `
        : css `
          &:hover {
            background-color: ${type === 'plus'
            ? theme.colors.PRIMARY_700
            : theme.colors.SNOW_LIGHT};
          }
          &:active {
            ${type === 'plus'
            ? `background-color: ${theme.colors.PRIMARY_600}`
            : ''}
          }
        `;
}}
`;
const NumberInput = (_a) => {
    var { value, step = 1, max, min, width = 5, status, inputSize = 'MEDIUM', innerRef = React.createRef(), block = false, disabled, e2eMessagesId, message, onChange, onFocus, onBlur, onKeyPress } = _a, restProps = __rest(_a, ["value", "step", "max", "min", "width", "status", "inputSize", "innerRef", "block", "disabled", "e2eMessagesId", "message", "onChange", "onFocus", "onBlur", "onKeyPress"]);
    const [isFocus, setIsFocus] = useState(false);
    const [isInputTooltipShow, setIsInputTooltipShow] = useState(false);
    const getRefCurrent = useCallback(() => {
        if (typeof innerRef === 'function' || !innerRef || !innerRef.current)
            return null;
        return innerRef.current;
    }, [innerRef]);
    const isAllowCalculator = useCallback((type) => value === undefined ||
        (type === 'plus' && (max === undefined || value < max)) ||
        (type === 'minus' && (min === undefined || value > min)), [max, min, value]);
    const handleOnChange = useCallback((event) => {
        onChange(parseInt(event.target.value || '0', 10));
    }, [onChange]);
    const handleFocus = useCallback((event) => {
        setIsFocus(true);
        event.target.select();
        if (onFocus)
            onFocus(event);
    }, [onFocus]);
    const handleBlur = useCallback((event) => {
        setIsFocus(false);
        if (onBlur)
            onBlur(event);
    }, [setIsFocus, onBlur]);
    const handleButtonClick = useCallback((type) => {
        if (!isAllowCalculator(type))
            return;
        const nextValue = type === 'plus' ? value + step : value - step;
        const isInvalidNumber = Number.isNaN(nextValue);
        if (type === 'plus' &&
            max !== undefined &&
            (isInvalidNumber || nextValue > max)) {
            onChange(max);
            return;
        }
        if (type === 'minus' &&
            min !== undefined &&
            (isInvalidNumber || nextValue < min)) {
            onChange(min);
            return;
        }
        onChange(nextValue);
    }, [isAllowCalculator, value, step, max, onChange, min]);
    const handleMouseEnter = () => {
        const currentRef = getRefCurrent();
        if (currentRef) {
            setIsInputTooltipShow(currentRef.scrollWidth > currentRef.clientWidth);
        }
    };
    const handleKeyPress = useCallback((event) => {
        const refCurrent = getRefCurrent();
        if (onKeyPress)
            onKeyPress(event, (refCurrent === null || refCurrent === void 0 ? void 0 : refCurrent.value) || '');
    }, [getRefCurrent, onKeyPress]);
    return (React.createElement(Wrapper, { block: block },
        React.createElement(InputWrapper, { status: status, block: block, disabled: disabled, focused: isFocus, width: width },
            React.createElement(ButtonWrapper, { type: "minus", inputSize: inputSize, onClick: () => handleButtonClick('minus'), disabled: !isAllowCalculator('minus') },
                React.createElement(Minus, { color: "INK_600", size: inputSize })),
            React.createElement(TooltipView, { type: "primary", lang: "en", text: value.toString(), show: isInputTooltipShow, direction: "TOP_CENTER", containerClassName: "inputWithIcon-tooltip" },
                React.createElement(StyledInput, Object.assign({}, restProps, { ref: innerRef, status: status, disabled: disabled, inputSize: inputSize, block: block, value: value, onChange: handleOnChange, onFocus: handleFocus, onBlur: handleBlur, onKeyPress: handleKeyPress, onMouseEnter: handleMouseEnter, onMouseLeave: () => setIsInputTooltipShow(false), inputMode: "numeric", type: "number" }))),
            React.createElement(ButtonWrapper, { type: "plus", inputSize: inputSize, onClick: () => handleButtonClick('plus'), disabled: !isAllowCalculator('plus') },
                React.createElement(Plus, { color: "INK_600", size: inputSize }))),
        message && (React.createElement(MessageWrapper, { "data-e2e-id": e2eMessagesId },
            React.createElement(StyledMessage, { status: status }, message)))));
};
export default memo(NumberInput);
//# sourceMappingURL=index.js.map